import React, { useEffect, useState } from 'react';

import ActionPendingChip from './ActionPendingChip';
import ActionPendingDialog from './ActionPendingDialog';
import { useWidgetSDK } from '../../widgets';

/**
 * ActionPendingControls — single drop-in for any visit detail surface.
 *
 * Renders:
 *   - The current overlay chip (if any).
 *   - A "Resolve" button (when the viewer holds the open overlay).
 *   - A "Forward" button (when the viewer holds the open overlay).
 *
 * Origination of new hops now lives in SendVisitDialog ("Send Visit"
 * button); the legacy standalone "Send for action" button has been
 * retired here.
 *
 * Stays passive — purely controls. Owning page is responsible for
 * keeping its `visit` prop in sync via store / WS subscriptions.
 *
 * Props:
 *   visit              — visit row.
 *   viewer             — { memberId, role }.
 *   onVisitChanged     — (visit) => void. Called with the post-action
 *                        visit row so the parent can re-render.
 *   defaultToRole      — optional pre-selection for the dialog
 *                        (used by AttachPlanModal in Phase 7).
 *   defaultIntent      — optional pre-selection.
 */
export default function ActionPendingControls({
  visit, viewer, onVisitChanged, defaultToRole = null, defaultIntent = null,
}) {
  const [setDialogOpen, setSetDialogOpen] = useState(false);
  const sdk = useWidgetSDK();

  const overlayHolder = visit?.action_pending_with_member_id;
  const isMyOverlay = !!overlayHolder && overlayHolder === viewer?.memberId;
  const visitId = visit?.visit_id;

  const onSetSubmitted = (resp) => {
    setSetDialogOpen(false);
    onVisitChanged?.(resp?.visit);
  };

  // Resolve flow moved into TaskResolutionWidget (PR 5 of the Widget
  // Library MVP). The widget fires `arogya:widgets:task-resolved` with
  // the API response; we subscribe to keep the existing onVisitChanged
  // contract working — list views / panels refresh as before.
  useEffect(() => {
    if (!visitId) return undefined;
    const onResolved = (e) => {
      const detail = e?.detail || {};
      if (detail.visitId !== visitId) return;
      const resp = detail.response;
      onVisitChanged?.(resp?.visit);
    };
    window.addEventListener('arogya:widgets:task-resolved', onResolved);
    return () => window.removeEventListener('arogya:widgets:task-resolved', onResolved);
  }, [visitId, onVisitChanged]);

  const openResolve = () => {
    if (!visit?.visit_id) return;
    sdk.openWidget('taskResolution', {
      visitId: visit.visit_id,
      prefill: { visit },
    }, { source: 'action-pending-controls' });
  };

  return (
    <>
      <div className="flex items-center gap-2 flex-wrap">
        <ActionPendingChip visit={visit} />
        {isMyOverlay && (
          <>
            <button
              type="button"
              onClick={openResolve}
              className="px-3 py-1.5 text-xs font-medium rounded-md bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              Resolve
            </button>
            <button
              type="button"
              onClick={() => setSetDialogOpen(true)}
              className="px-3 py-1.5 text-xs font-medium rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-700"
            >
              Forward
            </button>
          </>
        )}
      </div>

      {setDialogOpen && (
        <ActionPendingDialog
          visit={visit}
          viewer={viewer}
          defaultToRole={defaultToRole}
          defaultIntent={defaultIntent}
          onSubmitted={onSetSubmitted}
          onCancel={() => setSetDialogOpen(false)}
        />
      )}
    </>
  );
}
